import { useMemo } from 'react';

import type { User } from '@/api/common/auth';
import { hasAnyRole } from '@/api/common/auth/utils';
import { legacyMenuConfigs } from '@/config/navigation';
import { applyMenuOverrides, menuOverrides } from '@/config/navigation/menuOverrides';
import type { CodeString } from '@/consts/codes';
import { nativeMenu, type MenuItem } from '@/consts/navigation';
import { buildDynamicSection } from '@/features/navigation/utils/buildDynamicSection';
import { selectUser, useAuthStore } from '@/stores/useAuthStore';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { useRecentsStore } from '@/stores/useRecentsStore';

import { useRoleBasedMenu } from './useRoleBasedMenu';

export interface DynamicMenuResult {
  menu: MenuItem[];
  allMenuItems: MenuItem[];
  addRecent: (code: CodeString | undefined) => Promise<void>;
}

const filterMenuByRoles = (items: MenuItem[], user: User | null): MenuItem[] => {
  return items
    .filter((item) => {
      if (!item.requiredRoles || item.requiredRoles.length === 0) return true;
      return hasAnyRole(user, item.requiredRoles);
    })
    .map((item) => {
      if (!item.children) return item;
      const filteredChildren = filterMenuByRoles(item.children, user);
      return { ...item, children: filteredChildren };
    });
};

export const useDynamicMenu = (): DynamicMenuResult => {
  const user = useAuthStore(selectUser);

  // Get role-filtered legacy menu from JSON configs
  const legacyMenu = useRoleBasedMenu({ menuConfigs: legacyMenuConfigs });

  // Merge native (filtered by requiredRoles) and legacy menus, then apply overrides
  const fullMenu = useMemo(() => {
    const filteredNative = filterMenuByRoles(nativeMenu, user);
    const merged = [...filteredNative, ...legacyMenu];
    return applyMenuOverrides(merged, menuOverrides);
  }, [user, legacyMenu]);

  // Flatten all menu items with codes for tracking and dynamic sections (recursive for nested sections like Administration)
  const allMenuItems = useMemo(() => {
    const flatten = (items: MenuItem[]): MenuItem[] =>
      items.flatMap((item) => {
        const nested = item.children ? flatten(item.children) : [];
        return item.code ? [item, ...nested] : nested;
      });
    return fullMenu.flatMap((section) => flatten(section.children ?? []));
  }, [fullMenu]);

  const { favoriteCodes, favoritesLoading } = useFavoritesStore();
  const { recentCodes, recentsLoading, addRecent } = useRecentsStore();

  const menu = useMemo(() => {
    return fullMenu.map((section) => {
      if (section.text === 'navigation.favorites.title') {
        return buildDynamicSection(
          allMenuItems,
          section,
          favoriteCodes,
          favoritesLoading,
          'navigation.favorites.children.empty'
        );
      }
      if (section.text === 'navigation.recent.title') {
        return buildDynamicSection(
          allMenuItems,
          section,
          recentCodes,
          recentsLoading,
          'navigation.recent.children.empty'
        );
      }
      return section;
    });
  }, [fullMenu, allMenuItems, favoriteCodes, favoritesLoading, recentCodes, recentsLoading]);

  return { menu, allMenuItems, addRecent };
};
